import React, { FC, useState, useMemo, useCallback } from 'react'
import { FieldError, FormProvider } from 'react-hook-form'
import { FormModal, IFormModalProps } from 'transfix-ui/components/Modals/FormModal'
import { IModalAction } from 'transfix-ui/components/Modals/ModalAction'
import Grid from 'transfix-ui/core/Grid'
import {
  ICarrierApplicationStates,
  IComplianceCoverReviewStatuses,
  IShipmentStates,
  Maybe,
} from 'types/graphqlTypes'
import { useMPAnalytics } from 'apps/marketplace/hooks/useMPAnalytics'
import { isDigitalOption } from 'apps/marketplace/utils/helpers/isDigitalOption'
import { ICarrierRequirementsQuery } from '../CarrierStatusCard/graphql/CarrierRequirements'
import ShipmentDetailsCard from '../ShipmentDetailsCard'
import CoverageFormBody from './components/CoverageFormBody'
import CoverageFormBodySkeleton from './components/CoverageFormBody/CoverageFormBodySkeleton'
import { IShipmentCoverInfoQuery, useShipmentCoverInfoQuery } from './graphql/ShipmentCoverData'
import { useCoverModalForm } from './hooks/useCoverModalForm'
import { useCoverModalStyles } from './hooks/useCoverModalStyles'
import useCoverageMutations from './hooks/useCoverageMutations'
import { ICoverInfoCarrier, ICoverModalCarrierOption } from './types'
import { showCoverageWarning, getCoverModalSchema, isEmptyLocationValid } from './utils'

export interface ICoverModalProps extends Pick<IFormModalProps, 'onClose'> {
  shipmentId: string
  carrierOption?: ICoverModalCarrierOption
  onSuccess?: () => void
}

const CoverModal: FC<ICoverModalProps> = ({ carrierOption, onClose, onSuccess, shipmentId }) => {
  const { trackEvent } = useMPAnalytics()
  const inProd = __PROD__

  const { id: carrierOptionId } = carrierOption || {}

  const classes = useCoverModalStyles()

  // Mutations
  const { coverShipment, offerShipment } = useCoverageMutations({ shipmentId })

  // Form init
  const formMethods = useCoverModalForm({
    defaultValues: {
      useCustomCallback: 'no',
      callbackDate: new Date(),
    },
  })

  // Form schema validation errors
  const { errors: formErrors } = formMethods || {}
  const formValidationErrors: string[] = useMemo(() => {
    return Object.values(formErrors).map((entry: FieldError) => entry.message || '')
  }, [formErrors])

  // Carrier
  const [carrierMeetsRequirements, setCarrierMeetsRequirements] = useState<boolean>(false)
  const [complianceCoverReviewStatus, setComplianceCoverReviewStatus] = useState<
    IComplianceCoverReviewStatuses | undefined
  >()
  const [selectedCarrier, setSelectedCarrier] = useState<Maybe<ICoverInfoCarrier>>(null)
  const { state: selectedCarrierState } = selectedCarrier || {}

  // Booking Contact User Id
  const [selectedContactUserId, setSelectedContactUserId] = useState<string | undefined>(undefined)

  // Mutation Errors
  const [mutationErrors, setMutationErrors] = useState<string[]>([])

  // Form warnings
  const [formWarnings, setFormWarnings] = useState<string[]>([])

  // Form submission state
  const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false)

  const [isFormReady, setIsFormReady] = useState<boolean>(false)

  const setFormData = useCallback(
    (shipmentData: IShipmentCoverInfoQuery) => {
      const { shipment } = shipmentData || {}
      const { coveredBid } = shipment || {}
      const {
        carrierApplication: coveredBidCarrierApplication,
        carrierContactUser: coveredBidContactUser,
      } = coveredBid || {}

      const {
        carrierApplication: carrierOptionCarrierApplication,
        carrierContactUser: optionCarrierContactUser,
        createdBy,
        source,
      } = carrierOption || {}
      const isOptionDigital = isDigitalOption(source)
      const carrierOptionContactUser =
        isOptionDigital && !optionCarrierContactUser ? createdBy : optionCarrierContactUser

      const carrierContactUser = coveredBidContactUser || carrierOptionContactUser
      const { id: carrierContactUserId } = carrierContactUser || {}
      const carrier = coveredBidCarrierApplication || carrierOptionCarrierApplication

      if (carrierContactUserId) {
        setSelectedContactUserId(carrierContactUserId)
      }
      setSelectedCarrier(carrier)
      formMethods.reset(getCoverModalSchema({ shipment, carrierOption }))
      setIsFormReady(true)
    },
    [carrierOption, formMethods]
  )

  const { data: shipmentCoverData } = useShipmentCoverInfoQuery({
    variables: {
      id: shipmentId,
    },
    onCompleted: setFormData,
  })
  const { shipment } = shipmentCoverData || {}
  const { coveredBid, firstPickup, lastPickup, state: shipmentState } = shipment || {}
  const { carrierContactUser: currentDispatcher, driver: currentDriver } = coveredBid || {}
  const { appointment: firstPickupAppointment } = firstPickup || {}
  const { appointment: lastPickupAppointment } = lastPickup || {}

  const { appointmentType } = firstPickupAppointment || {}
  const appointment = appointmentType === 'range' ? firstPickupAppointment : lastPickupAppointment
  const { startAt: pickupTime, timezone: shipmentTimezone = '' } = appointment || {}

  const isShipmentCovered = shipmentState === IShipmentStates.covered
  const isShipmentOffered = shipmentState === IShipmentStates.offered

  const carrierRequirementsHandler = useCallback((carrierData: ICarrierRequirementsQuery) => {
    const { carrierComplianceMatch } = carrierData || {}
    const { complianceCoverReview, meetsRequirements } = carrierComplianceMatch || {}
    const { status } = complianceCoverReview || {}
    setCarrierMeetsRequirements(meetsRequirements)
    setComplianceCoverReviewStatus(status)
  }, [])

  const resetFormErrors = useCallback(() => {
    setMutationErrors([])
    setIsFormSubmitting(true)
  }, [])

  const appendError = useCallback((error: string) => {
    setMutationErrors(mutationErrors => [...mutationErrors, error])
  }, [])

  const appendWarning = useCallback((warning: string) => {
    setFormWarnings(formWarnings => [...formWarnings, warning])
  }, [])

  const setCoverageWarning = useCallback(
    (carrier: Maybe<ICoverInfoCarrier>) => {
      setFormWarnings([])
      const { name: carrierName } = carrier || {}
      if (inProd && showCoverageWarning(carrierName)) {
        appendWarning(`Warning: You are attempting to cover a load with ${carrierName} in PROD.`)
      }
    },
    [appendWarning, inProd]
  )

  const selectCarrierHandler = (carrier: Maybe<ICoverInfoCarrier>) => {
    setSelectedCarrier(carrier)
    setCoverageWarning(carrier)
  }

  const coverShipmentHandler = useCallback(() => {
    resetFormErrors()
    const canCover = !isShipmentCovered && !isShipmentOffered
    if (canCover) {
      formMethods.handleSubmit(
        async data => {
          const isValid = isEmptyLocationValid(data)
          if (!isValid) {
            appendError('Empty Driver Location required')
            setIsFormSubmitting(false)
            return
          }
          try {
            const { errors = [], shipmentId } = await coverShipment(data, shipmentTimezone)

            if (shipmentId && !errors.length) {
              if (carrierOptionId) {
                trackEvent('Carrier Options', 'COVERED_SHIPMENT_WITH_OUTBOUND_OPTION', {
                  shipmentId,
                  carrierOptionId,
                })
              }
              if (onSuccess) {
                onSuccess()
              }
              onClose()
            }

            if (errors.length) {
              if (carrierOptionId) {
                trackEvent('Carrier Options', 'ERROR_COVERING_SHIPMENT_WITH_OUTBOUND_OPTION', {
                  shipmentId,
                  carrierOptionId,
                  errors,
                })
              }
              setMutationErrors(errors.map(({ message }) => message))
              setIsFormSubmitting(false)
            }
          } catch (e) {
            setMutationErrors(['There was an error covering this shipment'])
          }
        },
        () => {
          setIsFormSubmitting(false)
        }
      )()
    } else {
      if (isShipmentCovered) {
        appendError('Shipment has already been covered')
      }
      if (isShipmentOffered) {
        appendError('Shipment has already been offered')
      }
      setIsFormSubmitting(false)
    }
  }, [
    appendError,
    carrierOptionId,
    coverShipment,
    formMethods,
    isShipmentCovered,
    isShipmentOffered,
    onClose,
    onSuccess,
    resetFormErrors,
    shipmentTimezone,
    trackEvent,
  ])

  const offerShipmentHandler = useCallback(() => {
    resetFormErrors()
    const isActiveCarrier = selectedCarrierState === ICarrierApplicationStates.activated
    const isBookingContactKnownUser = selectedContactUserId !== undefined
    const isCarrierCompliant =
      carrierMeetsRequirements ||
      (!carrierMeetsRequirements &&
        complianceCoverReviewStatus === IComplianceCoverReviewStatuses.approved)
    const canOffer = isActiveCarrier && isBookingContactKnownUser && isCarrierCompliant
    if (canOffer) {
      formMethods.handleSubmit(
        async data => {
          try {
            const { errors = [], shipmentId } = await offerShipment(data)

            if (shipmentId && !errors.length) {
              if (carrierOptionId) {
                trackEvent('Carrier Options', 'OFFERED_SHIPMENT_WITH_OUTBOUND_OPTION', {
                  shipmentId,
                  carrierOptionId,
                })
              }
              if (onSuccess) {
                onSuccess()
              }
              onClose()
            }

            if (errors.length) {
              if (carrierOptionId) {
                trackEvent('Carrier Options', 'ERROR_OFFERING_SHIPMENT_WITH_OUTBOUND_OPTION', {
                  shipmentId,
                  carrierOptionId,
                  errors,
                })
              }
              setMutationErrors(errors.map(({ message }) => message))
              setIsFormSubmitting(false)
            }
          } catch (e) {
            setMutationErrors(['There was an error offering this shipment'])
          }
        },
        () => {
          setIsFormSubmitting(false)
        }
      )()
    } else {
      if (!isActiveCarrier) {
        appendError('Carrier is not active')
      }
      if (!carrierMeetsRequirements) {
        appendError('Carrier is missing requirements')
      }
      if (complianceCoverReviewStatus === IComplianceCoverReviewStatuses.active_needs_review) {
        appendError('Compliance needs to be reviewed')
      }
      if (!isBookingContactKnownUser) {
        appendError('Booking Contact must be a known user')
      }
      setIsFormSubmitting(false)
    }
  }, [
    appendError,
    carrierMeetsRequirements,
    carrierOptionId,
    complianceCoverReviewStatus,
    formMethods,
    offerShipment,
    onClose,
    onSuccess,
    resetFormErrors,
    selectedCarrierState,
    selectedContactUserId,
    trackEvent,
  ])

  const actions: IModalAction[] = [
    {
      label: 'Cancel',
      type: 'dismiss',
      action: onClose,
      ButtonProps: { className: classes.cancelButton },
      disabled: isFormSubmitting || !isFormReady,
    },
    {
      label: 'Cover',
      type: 'confirm',
      dataTest: 'sd-coverage-form-cover-button',
      ButtonProps: {
        onClick: coverShipmentHandler,
      },
      disabled: isFormSubmitting || !isFormReady,
    },
    {
      label: 'Offer',
      type: 'confirm',
      dataTest: 'sd-coverage-form-offer-button',
      ButtonProps: {
        onClick: offerShipmentHandler,
      },
      disabled: isFormSubmitting || !isFormReady,
    },
  ]

  return (
    <FormModal
      open
      isClose
      title={`Cover shipment #${shipmentId}`}
      actions={actions}
      size={7}
      error={[...formWarnings, ...mutationErrors, ...formValidationErrors]}
      className={classes.modalContainer}
      onClose={onClose}
      dataTest='sd-coverage-modal'>
      <div className={classes.body}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <ShipmentDetailsCard shipmentId={shipmentId} />
          </Grid>
          {isFormReady ? (
            <Grid item xs={12}>
              <FormProvider {...formMethods}>
                {pickupTime && shipmentTimezone && (
                  <CoverageFormBody
                    shipmentId={shipmentId}
                    selectedCarrier={selectedCarrier}
                    onSelectCarrier={selectCarrierHandler}
                    setSelectedContactUserId={setSelectedContactUserId}
                    dispatcher={currentDispatcher}
                    isCovered={isShipmentCovered || isShipmentOffered}
                    onCarrierRequirementsLoaded={carrierRequirementsHandler}
                    pickupTime={pickupTime}
                    shipmentTimezone={shipmentTimezone}
                    driver={currentDriver}
                  />
                )}
              </FormProvider>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <CoverageFormBodySkeleton />
            </Grid>
          )}
        </Grid>
      </div>
    </FormModal>
  )
}

export default CoverModal
